import {
  MDBBtn,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalContent,
  MDBModalDialog,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBCard,
  MDBCardBody,
  MDBSelect,
} from "mdb-react-ui-kit";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { validatePhoneNumber } from "../popups/phonenumbers/AddRemoveNumbers";
import { Tooltip } from "react-tooltip";

const BusinessRegistrationForm = React.memo(
  ({
    brandInfo,
    setShowFullForm,
    showFullForm,
    onWebsiteChange,
    onBrandChange,
    onBrandNameChange,  // NEW: Added prop
  }) => {
    const [showWarning, setShowWarning] = useState(false);
    const { t, i18n } = useTranslation();
    const warningToggle = () => setShowWarning(!showWarning);

    // Get useForm parameters from FormProvider
    const {
      register,
      watch,
      setValue,
      control,
      unregister,
      clearErrors,
      formState: { errors },
    } = useFormContext();

    // NEW: Add timer ref for brand name similar to website
    const brandNameTimerRef = useRef(null);

    // NEW: Add this function to handle brand name changes with 1-second delay
    const handleBrandNameChangeDebounced = (value) => {
      // Clear any existing timer
      if (brandNameTimerRef.current) {
        clearTimeout(brandNameTimerRef.current);
      }

      // Set a new timer for 1 second
      brandNameTimerRef.current = setTimeout(() => {
        if (onBrandNameChange) {
          onBrandNameChange(value);
        }
      }, 1000);
    };

    // NEW: Add cleanup effect for the brand name timer
    useEffect(() => {
      return () => {
        if (brandNameTimerRef.current) {
          clearTimeout(brandNameTimerRef.current);
        }
      };
    }, []);

    // Toggle functions
    const handleAddNew = () => {
      // Unregister dropdown field
      unregister("brand_id");
      setShowFullForm(true);
    };

    const handleSelectBusiness = () => {
      // Unregister all form fields
      unregister("brand_details");
      setShowFullForm(false);
    };

    const handleOk = () => {
      setValue("brand_details.org_type", "", {
        shouldDirty: true,
      });
      warningToggle();
    };

    return (
      <>
        {/* Conditional rendering based on showFullForm state */}
        {!showFullForm ? (
          // Dropdown view
          <MDBCard className="container rounded-6 shadow-3-strong p-5">
            <MDBCardBody className="p-4 border border-2 rounded-6 shadow-3-strong ">
              <div className="d-flex align-items-center justify-content-between">
                <h4 className="mb-0">Business Registration</h4>
                <MDBBtn onClick={handleAddNew}>
                  <MDBIcon fas icon="plus" className="me-2" />
                  Add New
                </MDBBtn>
              </div>
              <div className="add-numbermain">
                <p className="mb-0">
                  Select Business Registration{" "}
                  <span className="required">*</span>
                </p>
                <div className="w-30">
                  <Controller
                    name="brand_id"
                    control={control}
                    rules={{ required: "Please select a Business Registration" }}
                    render={({ field }) => (
                  <MDBSelect
                    search
                        value={field.value || ""}
                    data={[
                      { text: "Select Business Registration", value: "" },
                      ...(brandInfo?.active_brands?.map((brand) => ({
                        text: `${brand.brand_name} (${brand?.brand_id})`,
                        value: brand.brand_id,
                      })) ?? []),
                    ]}
                    onChange={(data) => {
                      if (data && data.value) {
                            field.onChange(data.value);
                          } else {
                            field.onChange("");
                      }
                      if (onBrandChange) {
                        onBrandChange(data?.value);
                      }
                    }}
                  />
                    )}
                  />
                  {errors?.brand_id && (
                    <div
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875rem",
                        marginTop: "0.25rem",
                      }}
                    >
                      {errors.brand_id.message}
                    </div>
                  )}
                </div>
              </div>
            </MDBCardBody>
          </MDBCard>
        ) : (
          // Full form view
          <MDBCard className="container rounded-6 shadow-3-strong p-5 ">
            <MDBCardBody className="p-4 border border-2 rounded-6 shadow-3-strong position-relative">
              <div
                className="d-flex align-items-center justify-content-between"
                style={{ paddingLeft: "30px", paddingRight: "30px" }}
              >
                <h4 className="mb-0">Business Registration</h4>
                {brandInfo?.active_brands?.length > 0 && (
                  <MDBBtn onClick={handleSelectBusiness}>
                    Select Business Registration
                  </MDBBtn>
                )}
              </div>

              {/* Full Business Registration Form */}
              <div className="biz-form">
                <div className="container">
                  {/* Form Fields */}
                  <div className="form-row">
                    {/* Contact Name */}
                    <div className="form-group">
                      <label htmlFor="contact_name">
                        Business Contact Name{" "}
                        <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="contact_name"
                          maxLength={100}
                          name="brand_details.contact_name"
                          placeholder="Enter Your Full Name"
                          {...register("brand_details.contact_name", {
                            required: "Business Contact Name is required",
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.contact_name && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.contact_name.message}
                        </div>
                      )}
                    </div>

                    {/* Email */}
                    <div className="form-group">
                      <label htmlFor="email">
                        Email Address <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="email"
                          maxLength={100}
                          name="brand_details.email"
                          type="email"
                          placeholder=" Enter Your Email"
                          {...register("brand_details.email", {
                            setValueAs: (v) => v?.trim(),
                            required: "Email Address is required",
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address",
                            },
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.email && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.email.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    {/* Phone */}
                    <div className="form-group">
                      <label htmlFor="phno">
                        Phone Number <span className="required">*</span>
                      </label>
                      <div className="input-wrapper input-wrapper--focusring">
                        <input
                          id="phno"
                          name="brand_details.phno"
                          inputMode="tel"
                          placeholder="Enter up to 15 digits"
                          maxLength={15}
                          {...register("brand_details.phno", {
                            required: "Phone Number is required",
                            onChange: (e) => {
                              let digits = e.target.value.replace(/\D/g, "");
                              if (digits.length > 20) {
                                digits = digits.slice(0, 20);
                              }
                              setValue("brand_details.phno", digits, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            },
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.phno && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.phno.message}
                        </div>
                      )}
                    </div>

                    {/* Company Name */}
                    <div className="form-group">
                      <label htmlFor="company_name">
                        Legal Company Name <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="company_name"
                          maxLength={150}
                          name="brand_details.company_name"
                          placeholder="Enter Legal Company Name"
                          {...register("brand_details.company_name", {
                            required: "Legal Company Name is required",
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.company_name && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.company_name.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    {/* Brand Name (DBA) - UPDATED with onChange handler */}
                    <div className="form-group">
                      <label htmlFor="brand_name">
                        DBA Name <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="brand_name"
                          maxLength={150}
                          name="brand_details.brand_name"
                          placeholder="Doing Business As..."
                          {...register("brand_details.brand_name", {
                            required: "DBA Name is required",
                            onChange: (e) => {
                              // NEW: Call the debounced handler with the new value
                              handleBrandNameChangeDebounced(e.target.value);
                            },
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.brand_name && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.brand_name.message}
                        </div>
                      )}
                    </div>

                    {/* Tax Number */}
                    <div className="form-group">
                      <label htmlFor="tax_number">
                        EIN/Tax Number <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="tax_number"
                          name="brand_details.tax_number"
                          placeholder="EIN/Tax Number"
                          {...register("brand_details.tax_number", {
                            required: "EIN/Tax Number is required",
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.tax_number && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.tax_number.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    {/* Country - Modified with Controller */}
                    <div className="form-group">
                      <label htmlFor="country">
                        Issuing Country <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Controller
                          name="brand_details.country"
                          control={control}
                          rules={{
                            required: "Please select a country",
                          }}
                          render={({ field }) => (
                            <select
                              id="brand_details.country"
                              name="brand_details.country"
                              value={watch("brand_details.country") || ""}
                              onChange={(e) => {
                                e.preventDefault();
                                setValue(
                                  "brand_details.country",
                                  e.target.value,
                                  {
                                    shouldDirty: true,
                                  }
                                );
                                clearErrors("brand_details.country");
                              }}
                            >
                              <option value="">Select Country</option>
                              {brandInfo?.country_list?.map((country) => (
                                <option
                                  key={country.cscode}
                                  value={country.cscode}
                                >
                                  {country.cname}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.country && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.country.message}
                        </div>
                      )}
                    </div>

                    {/* Organization Type - Modified with Controller */}
                    <div className="form-group">
                      <label htmlFor="org_type">
                        Legal Formation <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Controller
                          name="brand_details.org_type"
                          control={control}
                          rules={{
                            required: "Please select Legal Formation type",
                          }}
                          render={({ field }) => (
                            <select
                              id="org_type"
                              name="brand_details.org_type"
                              value={
                                watch("brand_details.org_type")
                                  ? parseInt(watch("brand_details.org_type"))
                                  : ""
                              }
                              onChange={(e) => {
                                e.preventDefault();
                                if (parseInt(e.target.value) === 4) {
                                  warningToggle();
                                } else {
                                  setValue(
                                    "brand_details.org_type",
                                    e.target.value,
                                    {
                                      shouldDirty: true,
                                    }
                                  );
                                  clearErrors("brand_details.org_type");
                                }
                              }}
                            >
                              <option value="">Select Formation Type</option>
                              {brandInfo?.acc_org_type_list?.map((org) => (
                                <option
                                  key={org.org_type_id}
                                  value={parseInt(org.org_type_id)}
                                >
                                  {org.org_type_name}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.org_type && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.org_type.message}
                        </div>
                      )}
                    </div>

                    {/* Industry Type - Modified with Controller */}
                    <div className="form-group">
                      <label htmlFor="vertical_type">
                        Industry Vertical <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Controller
                          name="brand_details.vertical_type"
                          control={control}
                          rules={{
                            required: "Please select Industry Vertical type",
                          }}
                          render={({ field }) => (
                            <select
                              id="vertical_type"
                              name="brand_details.vertical_type"
                              value={
                                watch("brand_details.vertical_type")
                                  ? parseInt(
                                    watch("brand_details.vertical_type")
                                  )
                                  : ""
                              }
                              onChange={(e) => {
                                e.preventDefault();
                                setValue(
                                  "brand_details.vertical_type",
                                  e.target.value,
                                  {
                                    shouldDirty: true,
                                  }
                                );
                                clearErrors("brand_details.vertical_type");
                              }}
                            >
                              <option value="">Select Industry</option>
                              {brandInfo?.acc_industry_type_list?.map(
                                (industry) => (
                                  <option
                                    key={industry.industry_type_id}
                                    value={parseInt(industry.industry_type_id)}
                                  >
                                    {industry.industry_type}
                                  </option>
                                )
                              )}
                            </select>
                          )}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.vertical_type && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.vertical_type.message}
                        </div>
                      )}
                    </div>

                    {/* Website */}
                    <div className="form-group position-relative">
                      <label htmlFor="website">
                        Business Website / Online Presence{" "}
                        <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="website"
                          name="brand_details.website"
                          type="url"
                          placeholder="https://www.yourbusiness.com/privacy"
                          {...register("brand_details.website", {
                            required: "Business Website / Online Presence is required",
                           
                            onChange: (e) => {
                              // Call the parent handler with the new value
                              if (onWebsiteChange) {
                                onWebsiteChange(e.target.value);
                              }
                            },
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
                          />
                        </svg>
                      </div>
                     
                      <div style={{ position: "absolute", left: 0, right: 0, top: "100%", fontSize: "0.875rem", paddingTop: "0.25rem" }}>
                        {errors?.brand_details?.website ? (
                          <span style={{ color: "#dc3545" }}>
                            {errors.brand_details.website.type === "required"
                              ? <>{errors.brand_details.website.message} <MDBIcon fas icon="info-circle" color="danger" className="website-error-tooltip cursor-pointer" /></>
                              : <>Please enter a valid Business Website / Online Presence. <MDBIcon fas icon="info-circle" color="danger" className="website-error-tooltip cursor-pointer" /></>
                            }
                          </span>
                        ) : (
                          <span className="text-primary">
                            <b>Note:</b> Please provide the exact Privacy Policy URL <MDBIcon fas icon="info-circle" color="primary" className="website-hint-tooltip cursor-pointer" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Address Fields */}
                  <div className="form-row">
                    {/* Street */}
                    <div className="form-group">
                      <label htmlFor="street">
                        Street Address <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="street"
                          maxLength={100}
                          name="brand_details.street"
                          placeholder="123 Main Street"
                          {...register("brand_details.street", {
                            required: "Street Address is required",
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.street && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.street.message}
                        </div>
                      )}
                    </div>

                    {/* City */}
                    <div className="form-group">
                      <label htmlFor="city">
                        City <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="city"
                          maxLength={100}
                          name="brand_details.city"
                          placeholder="City"
                          {...register("brand_details.city", {
                            required: "City is required",
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.city && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.city.message}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    {/* State - Modified with Controller */}
                    <div className="form-group">
                      <label htmlFor="state">
                        State <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <Controller
                          name="brand_details.state"
                          control={control}
                          rules={{ required: "Please select a State" }}
                          render={({ field }) => (
                            <select
                              id="state"
                              name="brand_details.state"
                              value={
                                watch("brand_details.state")
                                  ? parseInt(watch("brand_details.state"))
                                  : ""
                              }
                              onChange={(e) => {
                                e.preventDefault();
                                setValue(
                                  "brand_details.state",
                                  e.target.value,
                                  {
                                    shouldDirty: true,
                                  }
                                );
                                clearErrors("brand_details.state");
                              }}
                            >
                              <option value="">Select State</option>
                              {brandInfo?.state_list?.map((state) => (
                                <option
                                  key={state.sid}
                                  value={parseInt(state.sid)}
                                >
                                  {state.sname}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m-6 3l6-3"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.state && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.state.message}
                        </div>
                      )}
                    </div>

                    {/* Zip Code */}
                    <div className="form-group">
                      <label htmlFor="zipcode">
                        Zip Code <span className="required">*</span>
                      </label>
                      <div className="input-wrapper">
                        <input
                          id="zipcode"
                          name="brand_details.zipcode"
                          placeholder="123456"
                          {...register("brand_details.zipcode", {
                            required: "Zip Code is required",
                          })}
                        />
                        <svg
                          className="input-icon"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m0 0V3a1 1 0 011 1v1M7 4V3a1 1 0 011-1v0a1 1 0 011 1v1m-4 0h10M5 8h14l-1 13H6L5 8z"
                          />
                        </svg>
                      </div>
                      {errors?.brand_details?.zipcode && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {errors.brand_details.zipcode.message}
                        </div>
                      )}
                    </div>

                      {watch("brand_details.org_type") == 1 && (
                        <>
                          <div className="form-group">

                            <label htmlFor="stockExchange">
                              Stock Exchange <span className="required">*</span>
                            </label>
                            <div className="input-wrapper">
                              <select
                                id="stockExchange"
                                className="form-select"
                                {...register("brand_details.stock_exchange", {
                                  // setValueAs: (value) => Number(value),
                                  required: "Stock Exchange is required",
                                })}
                              >
                                <option value="">Select Stock Exchange</option>
                                {brandInfo?.stock_list && brandInfo?.stock_list?.map((stock) => (
                                  <option value={stock.id}>{stock.stock_name}</option>
                                ))}
                              </select>

                              {/* Stock Exchange Icon (Building / Market) */}
                              <svg
                                className="input-icon"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"
                                />
                              </svg>
                            </div>
                            {errors?.brand_details?.stock_exchange && (
                              <p className="text-danger">
                                {errors?.brand_details?.stock_exchange?.message}
                              </p>
                            )}
                          </div>

                          <div className="form-group">
                            <label htmlFor="stockSymbol">
                              Stock Symbol <span className="required">*</span>
                            </label>
                            <div className="input-wrapper">
                              <input
                                id="stockSymbol"
                                maxLength={10}
                                type="text"
                                {...register("brand_details.stock_symbol", {
                                  required: "Stock Symbol is required",
                                })}
                              />
                              <svg
                                className="input-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                              >
                                {/* Trend line */}
                                <path d="M3 17l6-6 4 4 6-6" />

                                {/* Arrow head (centered) */}
                                <path d="M15 5h5v5" />
                              </svg>

                            </div>
                            {errors?.brand_details?.stock_symbol && (
                              <p className="text-danger">
                                {errors?.brand_details?.stock_symbol?.message}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                  </div>
                </div>
              </div>
            </MDBCardBody>
          </MDBCard>
        )}

        {showWarning && (
          <div className="inside-modal">
            <MDBModal
              open={showWarning}
              setShow={setShowWarning}
              tabIndex="-1"
              staticBackdrop
            >
              <MDBModalDialog size="md" centered>
                <MDBModalContent>
                  <MDBModalHeader>
                    <MDBModalTitle>Alert</MDBModalTitle>
                    <MDBBtn
                      className="btn-close"
                      color="none"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOk();
                      }}
                    />
                  </MDBModalHeader>
                  <MDBModalBody>
                    <div className="d-flex bg-warning bg-opacity-25 p-3 rounded-3 border border-2 border-warning">
                      <MDBIcon
                        fas
                        icon="exclamation-triangle"
                        className="me-3 fs-5 mt-1"
                        color="warning"
                      />
                      <div>
                        We are unable to create a brand with Organization type
                        as 'Sole Proprietor'. Please contact Carrier Services
                        @800.619.3688 for further assistance.
                      </div>
                    </div>
                  </MDBModalBody>
                  <MDBModalFooter>
                    <MDBBtn
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleOk();
                      }}
                    >
                      {t("ok")}
                    </MDBBtn>
                  </MDBModalFooter>
                </MDBModalContent>
              </MDBModalDialog>
            </MDBModal>
          </div>
        )}

        {/* Tooltip for hint icon — shown when there is NO website error */}
        <Tooltip anchorSelect=".website-hint-tooltip" place="right-start" className="exampleTooltipLight">
          <div style={{ maxWidth: "280px" }}>
            <p style={{ marginBottom: "8px" }}>
              Enter a valid Privacy Policy URL in one of the following formats:
            </p>
            <ul style={{ paddingLeft: "18px", marginBottom: "8px" }}>
              <li>domain.com/privacy-policy</li>
              <li>
                <a href="https://www.domain.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  www.domain.com/privacy-policy
                </a>
              </li>
              <li>
                <a href="https://domain.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  https://domain.com/privacy-policy
                </a>
              </li>
            </ul>
            <p style={{ margin: 0 }}>
              <strong>Note:</strong> URLs starting with http:// are not supported.
            </p>
          </div>
        </Tooltip>

        {/* Tooltip for error icon — shown when there IS a website error */}
        <Tooltip anchorSelect=".website-error-tooltip" place="right-start" className="exampleTooltipLight">
          <div style={{ maxWidth: "280px" }}>
            <p style={{ marginBottom: "6px", color: "#ff6b6b", fontWeight: "700" }}>Errors:</p>
            <ul style={{ paddingLeft: "16px", marginBottom: "10px", color: "#ff6b6b" }}>
              {(errors?.brand_details?.website?.message || "")
                .split("||")
                .filter(Boolean)
                .map((err, i) => (
                  <li key={i} style={{ marginBottom: "4px" }}>{err}</li>
                ))}
            </ul>
            <hr style={{ margin: "8px 0", borderColor: "#555" }} />
            <p style={{ marginBottom: "8px" }}>
              Enter a valid Privacy Policy URL in one of the following formats:
            </p>
            <ul style={{ paddingLeft: "18px", marginBottom: "8px" }}>
              <li>domain.com/privacy-policy</li>
              <li>
                <a href="https://www.domain.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  www.domain.com/privacy-policy
                </a>
              </li>
              <li>
                <a href="https://domain.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                  https://domain.com/privacy-policy
                </a>
              </li>
            </ul>
            <p style={{ margin: 0 }}>
              <strong>Note:</strong> URLs starting with http:// are not supported.
            </p>
          </div>
        </Tooltip>

      </>
    );
  }
);

BusinessRegistrationForm.displayName = "BusinessRegistrationForm";

export default BusinessRegistrationForm;